import { existsSync, readFileSync, writeFileSync } from "node:fs";

/**
 * Estimate token count from content.
 *
 * Uses a weighted ratio based on content composition instead of a flat 4-char divisor.
 * Markdown headers/whitespace compress better (~5 chars/token), code is denser (~3 chars/token),
 * and prose sits around ~3.5 chars/token for cl100k_base.
 */
export function estimateTokens(content: string): number {
  if (!content) return 0;
  const lines = content.split("\n");
  let codeChars = 0;
  let markdownChars = 0;
  let proseChars = 0;
  let inCodeBlock = false;

  for (const line of lines) {
    const len = line.length + 1; // +1 for newline
    if (line.startsWith("```")) {
      inCodeBlock = !inCodeBlock;
      markdownChars += len;
    } else if (inCodeBlock) {
      codeChars += len;
    } else if (/^#{1,6}\s|^\s*[-*]\s|^\s*\d+\.\s|^\|/.test(line)) {
      markdownChars += len;
    } else if (line.trim() === "") {
      markdownChars += len;
    } else {
      proseChars += len;
    }
  }

  // Weighted estimate: code ~3 chars/token, prose ~3.5, markdown ~5
  return Math.ceil(codeChars / 3 + proseChars / 3.5 + markdownChars / 5);
}

/** Estimate token count from a file on disk */
export function estimateFileTokens(absPath: string): number {
  try {
    return estimateTokens(readFileSync(absPath, "utf8"));
  } catch {
    return 0;
  }
}

/** Strip YAML frontmatter block from markdown content */
export function stripFrontmatter(content: string): string {
  return content.replace(/^---[\s\S]*?---\n*/, "");
}

/** Write file only if its content has changed — avoids unnecessary git noise */
export function writeIfChanged(filePath: string, content: string): boolean {
  if (existsSync(filePath)) {
    const current = readFileSync(filePath, "utf-8");
    if (current === content) return false;
  }
  writeFileSync(filePath, content, "utf-8");
  return true;
}
